// ─────────────────────────────────────────────────────────────────────────────
// Current Patient Provider - TypeScript Implementation  
// Context provider for managing current patient state across the application
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Current patient interface
interface CurrentPatient {
  id: string;
  name: string;
  fullName?: string;
  email?: string;
  phone?: string;
}

// Context interface
interface CurrentPatientContextType {
  currentPatient: CurrentPatient | null;
  setCurrentPatient: (patient: CurrentPatient | null) => void;
  clearCurrentPatient: () => void;
  updateCurrentPatient: (updates: Partial<CurrentPatient>) => void;
}

// Create context
const CurrentPatientContext = createContext<CurrentPatientContextType | undefined>(undefined);

// Provider props
interface CurrentPatientProviderProps {
  children: ReactNode;
}

// Provider component
export const CurrentPatientProvider: React.FC<CurrentPatientProviderProps> = ({ children }) => {
  const [currentPatient, setCurrentPatientState] = useState<CurrentPatient | null>(null);

  const setCurrentPatient = useCallback((patient: CurrentPatient | null) => {
    setCurrentPatientState(patient);
    
    // Optionally store in localStorage for persistence
    if (patient) {
      localStorage.setItem('currentSelectedPatient', JSON.stringify(patient));
    } else {
      localStorage.removeItem('currentSelectedPatient');
    }
  }, []);

  const clearCurrentPatient = useCallback(() => {
    setCurrentPatientState(null);
    localStorage.removeItem('currentSelectedPatient');
  }, []);

  const updateCurrentPatient = useCallback((updates: Partial<CurrentPatient>) => {
    setCurrentPatientState(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      localStorage.setItem('currentSelectedPatient', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Initialize from localStorage on mount
  React.useEffect(() => {
    const stored = localStorage.getItem('currentSelectedPatient');
    if (stored) {
      try {
        const patient = JSON.parse(stored);
        setCurrentPatientState(patient);
      } catch (error) {
        console.error('Failed to parse stored patient data:', error);
        localStorage.removeItem('currentSelectedPatient');
      }
    }
  }, []);

  const value: CurrentPatientContextType = {
    currentPatient,
    setCurrentPatient,
    clearCurrentPatient,
    updateCurrentPatient,
  };

  return (
    <CurrentPatientContext.Provider value={value}>
      {children}
    </CurrentPatientContext.Provider>
  );
};

// Hook to use current patient context
export const useCurrentPatient = (): CurrentPatientContextType => {
  const context = useContext(CurrentPatientContext);
  if (context === undefined) {
    throw new Error('useCurrentPatient must be used within a CurrentPatientProvider');
  }
  return context;
};

export type { CurrentPatient, CurrentPatientContextType };
